/**
 * Trunnion AI - Designer Canvas
 * React Flow canvas with drag-and-drop from palette.
 */

import { useCallback, type DragEvent } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  BackgroundVariant,
  useReactFlow,
  type Node,
  type EdgeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { nodeTypes } from './nodes/nodeRegistry';
import { edgeTypes } from './edges/edgeRegistry';
import { defaultNodeData, generateNodeId } from './nodes/nodeRegistry';
import { useWorkflowDesignerStore } from '@/stores/workflowDesignerStore';
import type { WorkflowNodeType, WorkflowNodeData } from '@/types';

export function DesignerCanvas() {
  const {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    addNode,
    setSelectedNodeId,
    setSelectedEdgeId,
    clearSelection,
    setViewport,
  } = useWorkflowDesignerStore();

  const { screenToFlowPosition } = useReactFlow();

  const onDragOver = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault();

      const type = event.dataTransfer.getData(
        'application/trunnion-node-type',
      ) as WorkflowNodeType;

      if (!type || !defaultNodeData[type]) return;

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newNode: Node<WorkflowNodeData> = {
        id: generateNodeId(type),
        type,
        position,
        data: defaultNodeData[type](),
      };

      addNode(newNode);
      setSelectedNodeId(newNode.id);
    },
    [screenToFlowPosition, addNode, setSelectedNodeId],
  );

  const onNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      setSelectedNodeId(node.id);
    },
    [setSelectedNodeId],
  );

  const onEdgeClick = useCallback(
    (_event: React.MouseEvent, edge: { id: string }) => {
      setSelectedEdgeId(edge.id);
    },
    [setSelectedEdgeId],
  );

  const onPaneClick = useCallback(() => {
    clearSelection();
  }, [clearSelection]);

  return (
    <div className="flex-1 h-full">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onNodeClick={onNodeClick}
        onEdgeClick={onEdgeClick}
        onPaneClick={onPaneClick}
        onMoveEnd={(_event, viewport) => setViewport(viewport)}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes as EdgeTypes}
        defaultEdgeOptions={{
          type: 'dataFlow',
          animated: true,
        }}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        snapToGrid
        snapGrid={[20, 20]}
        deleteKeyCode="Delete"
        multiSelectionKeyCode="Shift"
        proOptions={{ hideAttribution: true }}
        className="bg-trunnion-slate-950"
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1}
          color="rgba(255,255,255,0.05)"
        />
        <Controls
          showInteractive={false}
          className="!bg-trunnion-slate-800 !border-trunnion-slate-700"
        />
        <MiniMap
          nodeColor={() => '#D4A017'}
          maskColor="rgba(10, 14, 23, 0.8)"
          className="!bg-trunnion-slate-900 !border-trunnion-slate-700"
        />
      </ReactFlow>
    </div>
  );
}
